import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ChoiceChip } from 'common/ui/Chip';
import { Stack } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import { MODALITIES } from 'config/constants';
import spacings from 'styles/spacings';
import { Colors } from 'styles';
import { css } from 'styled-components';

type SpecificImpressionModalitiesProps = {
  modalities: readonly string[];
  onToggleModality: (modality: string) => void;
  onSelectAllModalities: () => void;
};

export const SpecificImpressionModalities = ({
  modalities,
  onToggleModality,
  onSelectAllModalities,
}: SpecificImpressionModalitiesProps): React.ReactElement => (
  <>
    <Stack
      alignY="center"
      stretchX
      wrap
      css={css`
        justify-content: flex-end;
        gap: ${spacings.xsmall}rem;
      `}
    >
      {MODALITIES.map((option) => {
        const isSelected = modalities.includes(option.value);

        return (
          <ChoiceChip
            key={option.value}
            size="xs"
            isSelected={isSelected}
            data-testid={`impression-modality-chip-${option.value}`}
            onClick={() => onToggleModality(option.value)}
            css={css`
              border: 1px solid ${isSelected ? Colors.blue4 : Colors.gray5};
              background: ${isSelected ? Colors.blue4 : 'transparent'};
              color: ${Colors.white};
              ${!isSelected &&
              css`
                &:focus,
                &:focus-within {
                  border-color: ${Colors.gray5};
                  box-shadow: none;
                }
              `}
            `}
          >
            {option.name}
          </ChoiceChip>
        );
      })}
    </Stack>
    <div
      data-testid="impression-modality-helper-message"
      css={css`
        display: flex;
        align-items: flex-start;
        gap: ${spacings.xsmall}rem;
        border: 1px solid ${Colors.blue4};
        border-radius: 8px;
        padding: ${spacings.small}rem;
        color: ${Colors.gray3};
      `}
    >
      <InfoOutlinedIcon sx={{ color: Colors.blue4, fontSize: 16, marginTop: '2px' }} />
      <Text variant="body2">
        Auto-Impression uses the modality tag sent with each exam. Inconsistent or non-standard
        values may prevent it from triggering.{' '}
        <button
          type="button"
          data-testid="impression-modality-all-modalities-link"
          onClick={onSelectAllModalities}
          css={css`
            padding: 0;
            border: 0;
            background: transparent;
            color: ${Colors.blue4};
            cursor: pointer;
            font: inherit;
            line-height: inherit;

            &:focus-visible {
              outline: 1px solid ${Colors.blue4};
              border-radius: 4px;
            }
          `}
        >
          <strong>
            Select <em>"All modalities"</em>
          </strong>
        </button>{' '}
        to ensure it always runs.
      </Text>
    </div>
  </>
);
